import { HStack, VStack, Box, Button, Image, useDisclosure, Text } from '@chakra-ui/react';
import { useTheme } from '../../../contexts/ThemeProvider';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { numberToBRL } from '../../../utils';
import { RiInformationFill } from 'react-icons/ri';
import { InfoModal } from './InfoModal';
import { ISales } from '@/service/club/types';
import { cacheBust } from '@/utils/cacheBust';

export function CardHistory({ valid_until, sale, code, payment_status, vindi_bill_url }: ISales) {
  const { created_at, amount, discount, payment_status: salePaymentStatus, vindi_bill_url: saleBillUrl } = sale;
  const infoModal = useDisclosure();

  const company = discount?.company;
  const how_to_use = discount?.how_to_use || '';
  const normalizedPaymentStatus = (salePaymentStatus || payment_status || '').toLowerCase();
  const billUrl = saleBillUrl || vindi_bill_url;
  const hasPaymentStatus = normalizedPaymentStatus.length > 0;
  const isPaymentPending = hasPaymentStatus && normalizedPaymentStatus !== 'paid';

  const { colors } = useTheme();

  const formattedDate = format(new Date(created_at), 'dd MMM yyyy', { locale: ptBR });
  const formattedDateValid = format(new Date(valid_until), 'dd MMM yyyy', { locale: ptBR });
  const statusLabel = hasPaymentStatus ? (isPaymentPending ? 'Pendente' : 'Pago') : null;

  return (
    <>
      <HStack
        justify="space-between"
        align="flex-start"
        py={3}
        borderBottom="1px solid #EEF1F2"
        spacing={3}
        width="100%"
      >
        {company?.logo && (
          <Image
            src={cacheBust(company.logo)}
            alt={company.name}
            boxSize="48px"
            minW="48px"
            objectFit="contain"
            borderRadius="12px"
            bg="#F4F6F7"
            p="4px"
          />
        )}

        <Box flex="1" minW={0}>
          <HStack justify="space-between" align="flex-start" spacing={3}>
            <Box minW={0}>
              <Text fontFamily="Glory" fontWeight={500} color="#292927" noOfLines={1}>
                {company?.name}
              </Text>
              <Text fontSize="12px" color="#747474">
                {[statusLabel || 'Compra física', formattedDate].filter(Boolean).join(' · ')}
              </Text>
              {(code && !isPaymentPending) || valid_until ? (
                <Text fontSize="12px" color="#747474" noOfLines={1}>
                  {[code && !isPaymentPending ? `Voucher ${code}` : null, `Válido até ${formattedDateValid}`]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              ) : null}
            </Box>

            <VStack align="flex-end" spacing={1} flexShrink={0}>
              <Text fontFamily="Glory" fontWeight={600} color={colors.primaryColor} whiteSpace="nowrap">
                {numberToBRL(Number(amount))}
              </Text>
              {how_to_use && (
                <Button
                  onClick={() => infoModal.onOpen()}
                  variant="ghost"
                  size="sm"
                  minW="auto"
                  px={1}
                  color={colors.primaryColor}
                  aria-label="Como usar"
                >
                  <RiInformationFill size={18} />
                </Button>
              )}
            </VStack>
          </HStack>

          {isPaymentPending && billUrl && (
            <Button
              as="a"
              href={billUrl}
              target="_blank"
              rel="noreferrer"
              size="sm"
              mt={2}
              borderRadius="full"
              bg={colors.primaryColor}
              color="white"
              _hover={{ opacity: 0.9 }}
            >
              Pagar agora
            </Button>
          )}
        </Box>
      </HStack>

      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={infoModal.onClose}
        showName={company?.name || ''}
        how_to_use={how_to_use || ''}
      />
    </>
  );
}
